import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  getCart, 
  addToCart as addToCartUtil, 
  updateCartItemQuantity as updateQuantityUtil,
  removeFromCart as removeItemUtil,
  clearCart as clearCartUtil,
  Cart, 
  CartItem 
} from '../utils/cart';
import { DbProduct } from '../api/db';

interface CartContextType {
  cart: Cart;
  isLoading: boolean;
  error: string | null;
  addToCart: (product: DbProduct, quantity?: number, imageUrl?: string) => {
    success: boolean;
    message?: string;
  };
  updateQuantity: (productId: string, quantity: number) => {
    success: boolean;
    message?: string;
  };
  removeItem: (productId: string) => void;
  clearCart: () => void;
  clearError: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<Cart>(getCart());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear error message
  const clearError = () => {
    setError(null);
  };

  // Functions that update cart and sync state
  const addToCart = (product: DbProduct, quantity = 1, imageUrl?: string) => {
    setIsLoading(true);
    clearError();
    
    const result = addToCartUtil(product, quantity, imageUrl);
    setCart(result.cart);
    
    if (!result.success && result.message) {
      setError(result.message);
    }
    
    setIsLoading(false);
    return {
      success: result.success,
      message: result.message
    };
  };

  const updateQuantity = (productId: string, quantity: number) => {
    setIsLoading(true);
    clearError();
    
    const result = updateQuantityUtil(productId, quantity);
    setCart(result.cart);
    
    if (!result.success && result.message) {
      setError(result.message);
    }
    
    setIsLoading(false);
    return {
      success: result.success,
      message: result.message
    };
  };

  const removeItem = (productId: string) => {
    setIsLoading(true);
    const updatedCart = removeItemUtil(productId);
    setCart(updatedCart);
    setIsLoading(false);
  };

  const clearCart = () => {
    setIsLoading(true);
    const updatedCart = clearCartUtil();
    setCart(updatedCart);
    setIsLoading(false);
  };

  // Periodically check for cart updates from other tabs/windows
  useEffect(() => {
    const checkForCartUpdates = () => {
      const currentCart = getCart();
      // Only update if the cart has changed (compare lastUpdated)
      if (currentCart.lastUpdated !== cart.lastUpdated) {
        setCart(currentCart);
      }
    };

    const intervalId = setInterval(checkForCartUpdates, 2000);
    return () => clearInterval(intervalId);
  }, [cart.lastUpdated]);

  return (
    <CartContext.Provider value={{ 
      cart, 
      isLoading, 
      error,
      addToCart, 
      updateQuantity, 
      removeItem, 
      clearCart,
      clearError
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}; 